// Componentes básicos e consistentes da interface.
import clsxBase, { type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { AlertTriangle, Inbox, Loader2, X } from 'lucide-react';
import { cloneElement, isValidElement, useEffect, type ButtonHTMLAttributes, type ComponentProps, type ReactElement, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { errorMessage } from '../lib/api.ts';

/** clsx + resolução de conflitos do Tailwind (ex.: w-full vs w-auto). */
export const clsx = (...v: ClassValue[]) => twMerge(clsxBase(...v));

type Variant = 'primary' | 'accent' | 'secondary' | 'ghost' | 'danger';

export function Button({ variant = 'secondary', size = 'md', loading, className, children, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: 'sm' | 'md'; loading?: boolean }) {
  return (
    <button
      {...rest}
      disabled={rest.disabled || loading}
      className={clsx(
        'inline-flex items-center justify-center gap-1.5 rounded-md font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        size === 'sm' ? 'h-7 px-2.5 text-xs' : 'h-9 px-3.5 text-sm',
        variant === 'primary' && 'bg-navy-900 text-white hover:bg-navy-800',
        variant === 'accent' && 'bg-accent-500 text-white hover:bg-accent-600',
        variant === 'secondary' && 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50',
        variant === 'ghost' && 'text-slate-600 hover:bg-slate-100',
        variant === 'danger' && 'bg-red-600 text-white hover:bg-red-700',
        className,
      )}
    >
      {loading && <Loader2 className="size-4 animate-spin" />}
      {children}
    </button>
  );
}

export function Card({ title, actions, className, children, padded = true }: { title?: ReactNode; actions?: ReactNode; className?: string; children: ReactNode; padded?: boolean }) {
  return (
    <section className={clsx('rounded-lg border border-slate-200 bg-white shadow-sm', className)}>
      {(title || actions) && (
        <header className="flex items-center justify-between gap-2 border-b border-slate-100 px-4 py-2.5">
          <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
          {actions && <div className="flex items-center gap-1.5">{actions}</div>}
        </header>
      )}
      <div className={padded ? 'p-4' : ''}>{children}</div>
    </section>
  );
}

export function Spinner({ label = 'Carregando…' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-10 text-sm text-slate-500" role="status">
      <Loader2 className="size-4 animate-spin" /> {label}
    </div>
  );
}

export function EmptyState({ title, children, icon }: { title: string; children?: ReactNode; icon?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-4 py-10 text-center">
      <div className="text-slate-300">{icon ?? <Inbox className="size-8" />}</div>
      <p className="text-sm font-medium text-slate-700">{title}</p>
      {children && <div className="text-sm text-slate-500">{children}</div>}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return (
    <div role="alert" className="flex items-start gap-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
      <AlertTriangle className="mt-0.5 size-4 shrink-0" />
      <div className="flex-1 whitespace-pre-line">{errorMessage(error)}</div>
      {onRetry && (
        <Button size="sm" onClick={onRetry}>
          Tentar de novo
        </Button>
      )}
    </div>
  );
}

type Labelled = ReactElement<{ 'aria-label'?: string }>;
/** Nome acessível exato para o campo (sem o texto das opções de um select dentro do label). */
function withLabel(children: ReactNode, label: string): ReactNode {
  if (!isValidElement(children)) return children;
  const c = children as Labelled;
  const isField = typeof c.type === 'string' ? ['input', 'select', 'textarea'].includes(c.type) : c.type === Input || c.type === Select || c.type === Textarea;
  return isField && !c.props['aria-label'] ? cloneElement(c, { 'aria-label': label }) : children;
}

export function Field({ label, hint, error, children, className }: { label: string; hint?: string; error?: string; children: ReactNode; className?: string }) {
  return (
    <label className={clsx('block', className)}>
      <span className="mb-1 block text-xs font-medium text-slate-600">{label}</span>
      {withLabel(children, label)}
      {hint && !error && <span className="mt-1 block text-xs text-slate-400">{hint}</span>}
      {error && <span className="mt-1 block text-xs text-red-600">{error}</span>}
    </label>
  );
}

const inputCls = 'w-full rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-800 placeholder:text-slate-400 focus:border-navy-500 focus:outline-none focus:ring-2 focus:ring-navy-100 disabled:bg-slate-50';

export const Input = ({ className, ...p }: InputHTMLAttributes<HTMLInputElement>) => <input {...p} className={clsx(inputCls, className)} />;
export const Textarea = ({ className, ...p }: ComponentProps<'textarea'>) => <textarea {...p} className={clsx(inputCls, 'font-mono text-[13px]', className)} />;
export const Select = ({ className, children, ...p }: SelectHTMLAttributes<HTMLSelectElement>) => (
  <select {...p} className={clsx(inputCls, 'pr-8', className)}>
    {children}
  </select>
);

export function Modal({ open, onClose, title, children, footer, wide }: { open: boolean; onClose: () => void; title: string; children: ReactNode; footer?: ReactNode; wide?: boolean }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-navy-950/40 p-4 sm:pt-16" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div role="dialog" aria-modal="true" aria-label={title} className={clsx('w-full rounded-lg bg-white shadow-xl', wide ? 'max-w-3xl' : 'max-w-lg')}>
        <header className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <h2 className="text-base font-semibold text-slate-900">{title}</h2>
          <button onClick={onClose} className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700" aria-label="Fechar">
            <X className="size-4" />
          </button>
        </header>
        <div className="max-h-[70vh] overflow-y-auto p-4">{children}</div>
        {footer && <footer className="flex justify-end gap-2 border-t border-slate-100 px-4 py-3">{footer}</footer>}
      </div>
    </div>
  );
}

export function Stat({ label, value, tone = 'default', hint, to }: { label: string; value: ReactNode; tone?: 'default' | 'danger' | 'warning' | 'good' | 'accent'; hint?: string; to?: string }) {
  const body = (
    <>
      <div className="text-xs font-medium text-slate-500">{label}</div>
      <div
        className={clsx(
          'mt-1 text-2xl font-semibold tabular-nums',
          tone === 'default' && 'text-slate-900',
          tone === 'danger' && 'text-red-600',
          tone === 'warning' && 'text-amber-600',
          tone === 'good' && 'text-emerald-600',
          tone === 'accent' && 'text-accent-600',
        )}
      >
        {value}
      </div>
      {hint && <div className="mt-0.5 text-xs text-slate-400">{hint}</div>}
    </>
  );
  const cls = 'block rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm';
  return to ? (
    <a href={to} className={clsx(cls, 'transition-colors hover:border-navy-300')}>
      {body}
    </a>
  ) : (
    <div className={cls}>{body}</div>
  );
}

export function Toast({ message, kind = 'success', onClose }: { message: string | null; kind?: 'success' | 'error'; onClose: () => void }) {
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(onClose, kind === 'error' ? 8000 : 3000);
    return () => clearTimeout(t);
  }, [message, kind, onClose]);
  if (!message) return null;
  return (
    <div
      role={kind === 'error' ? 'alert' : 'status'}
      className={clsx(
        'fixed right-4 bottom-4 z-[60] max-w-sm whitespace-pre-line rounded-md px-4 py-3 text-sm text-white shadow-lg',
        kind === 'success' ? 'bg-navy-900' : 'bg-red-700',
      )}
    >
      {message}
    </div>
  );
}
